import { type ReactNode, useEffect } from "react";
import { Navigate, useLocation } from "react-router";
import {
  getConfirmedOrg,
  getConfirmedTeam,
  markConfirmedOrg,
  markConfirmedTeam,
  noteUserAndResetIfChanged,
} from "../lib/active-selection.js";
import {
  useActiveOrgId,
  useActiveTeamId,
  useMyOrganizations,
  useMyTeams,
} from "../lib/use-active-org.js";

interface SelectionGateProps {
  /** Logged-in user id from the license claims — drives "fresh login" detection. */
  userId: string | null;
  children: ReactNode;
}

/**
 * Wraps the main `<Routes>`. After a fresh login (or whenever the active
 * org/team hasn't been explicitly confirmed in this client), forces the
 * coach through `/select/org` (when membership > 1) and then
 * `/select/team` (when reachable teams > 1).
 *
 * Single-choice cases short-circuit: a coach in exactly one club with
 * exactly one team is stamped as confirmed on the spot so the cockpit
 * loads immediately on every subsequent boot.
 *
 * The `/select/*` routes themselves render through this gate; we just
 * pass them through to avoid an infinite redirect.
 */
export function SelectionGate({ userId, children }: SelectionGateProps) {
  const location = useLocation();
  const orgs = useMyOrganizations();
  const teams = useMyTeams();
  const activeOrgId = useActiveOrgId();
  const activeTeamId = useActiveTeamId();

  // Drop confirmation markers on account change so a different coach on
  // the same device always sees the picker again.
  useEffect(() => {
    noteUserAndResetIfChanged(userId);
  }, [userId]);

  // Allow the select pages to render even if their preconditions
  // technically aren't met (single org / no teams) — they degrade
  // gracefully to a "nothing to choose" empty state.
  if (location.pathname.startsWith("/select/")) {
    return <>{children}</>;
  }

  // The /me payload is fetched async; until it arrives we render the
  // children so existing offline-cached state can show. The gate
  // re-evaluates as soon as /me lands.
  const meLoaded = orgs.length > 0;
  if (!meLoaded) {
    return <>{children}</>;
  }

  const confirmedOrg = getConfirmedOrg();
  const orgNeedsPick = orgs.length > 1 && confirmedOrg !== activeOrgId;
  if (orgNeedsPick) {
    return <Navigate to="/select/org" replace />;
  }
  // Single-org coach — auto-confirm so we never prompt for it.
  if (orgs.length === 1 && activeOrgId && confirmedOrg !== activeOrgId) {
    markConfirmedOrg(activeOrgId);
  }

  const confirmedTeam = getConfirmedTeam();
  const teamNeedsPick = teams.length > 1 && confirmedTeam !== activeTeamId;
  if (teamNeedsPick) {
    return <Navigate to="/select/team" replace />;
  }
  if (teams.length <= 1 && activeTeamId && confirmedTeam !== activeTeamId) {
    markConfirmedTeam(activeTeamId);
  }

  return <>{children}</>;
}
